/**
 * Loading placeholders.
 *
 * Sized to match the real content they stand in for — the table skeleton
 * takes the same `rowHeight` the table will use — so nothing jumps when
 * data arrives. Layout shift in a dense grid of numbers is disorienting in
 * a way it is not on a marketing page.
 */

import { cn } from '@/lib/cn';

export function Skeleton({
  className,
  rounded = 'rounded',
}: {
  className?: string;
  rounded?: string;
}) {
  return <div aria-hidden className={cn('skeleton-shimmer bg-surface-2', rounded, className)} />;
}

/** Table rows. Pass the same `rowHeight` the real table uses. */
export function SkeletonRows({
  rows = 8,
  columns = 5,
  rowHeight = 26,
  className,
}: {
  rows?: number;
  columns?: number;
  rowHeight?: number;
  className?: string;
}) {
  // Ragged widths on purpose: a grid of identical bars reads as a broken
  // layout, whereas uneven ones read as content arriving.
  const widths = ['w-full', 'w-4/5', 'w-3/5', 'w-2/3', 'w-1/2'];
  return (
    <div className={cn('flex flex-col', className)} role="status" aria-label="Loading rows">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex items-center gap-3 border-b border-border/50 px-3"
          style={{ height: rowHeight }}
        >
          {Array.from({ length: columns }, (_, columnIndex) => (
            <div key={columnIndex} className="flex-1">
              <Skeleton
                className={cn('h-2.5', widths[(rowIndex + columnIndex) % widths.length])}
                rounded="rounded-sm"
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonChart({ className }: { className?: string }) {
  return (
    <div
      role="status"
      aria-label="Loading chart"
      className={cn('flex h-full w-full flex-col gap-2 p-3', className)}
    >
      <Skeleton className="h-3 w-32" />
      <Skeleton className="min-h-24 flex-1" rounded="rounded-md" />
      <div className="flex gap-2">
        <Skeleton className="h-2.5 w-16" />
        <Skeleton className="h-2.5 w-16" />
        <Skeleton className="h-2.5 w-16" />
      </div>
    </div>
  );
}

export function SkeletonStatTiles({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="status" aria-label="Loading stats">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="rounded-md border border-border bg-surface-1 p-2.5">
          <Skeleton className="mb-2 h-2 w-14" />
          <Skeleton className="h-5 w-20" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonText({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)} role="status" aria-label="Loading">
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={cn('h-2.5', index === lines - 1 ? 'w-2/3' : 'w-full')}
          rounded="rounded-sm"
        />
      ))}
    </div>
  );
}
